import React, { useEffect, useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, Legend } from 'recharts';
import { useAppStore } from '../context/AppStore';
import { analyticsExtraAPI } from '../api/client';
import { Card, StatCard, Badge, Button, TabBar, NetworkIcon, ProgressBar, Spinner } from '../components/ui';
import { NETWORK_CONFIG } from '../api/mockData';

const COLORS = ['#7c3aed', '#2563eb', '#10b981', '#f59e0b', '#ef4444', '#06b6d4'];

export default function AnalyticsPage() {
  const { analytics, analyticsLoading, fetchAnalytics } = useAppStore();
  const [period, setPeriod] = useState('30d');
  const [bestHours, setBestHours] = useState({});
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchAnalytics();
    analyticsExtraAPI.bestHours().then(r => setBestHours(r.data)).catch(() => {});
  }, []);

  if (analyticsLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '300px', flexDirection: 'column', gap: '16px' }}>
        <Spinner size={40} />
        <p style={{ color: 'var(--text-muted)' }}>Cargando analytics...</p>
      </div>
    );
  }

  if (!analytics) return null;

  const pieData = analytics.by_network.map(n => ({
    name: NETWORK_CONFIG[n.network]?.name || n.network,
    value: n.reach,
    color: NETWORK_CONFIG[n.network]?.color || '#888',
  }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Period selector */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <TabBar
          tabs={[{ id: '7d', label: '7 días' }, { id: '30d', label: '30 días' }, { id: '90d', label: '90 días' }]}
          active={period}
          onChange={setPeriod}
        />
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <Badge color="var(--accent-success)">Actualizado hace 5 min</Badge>
          <Button
            size="sm" variant="secondary" icon="📥"
            loading={exporting}
            onClick={async () => {
              setExporting(true);
              try {
                const res = await analyticsExtraAPI.exportCsv();
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const a = document.createElement('a');
                a.href = url; a.download = 'txflow-analytics.csv'; a.click();
                window.URL.revokeObjectURL(url);
              } catch { } finally { setExporting(false); }
            }}
          >
            Exportar CSV
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="animate-fadeIn" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '14px' }}>
        <StatCard icon="📤" label="Posts" value={analytics.totals.posts} growth={analytics.growth.posts} color="var(--accent-primary)" />
        <StatCard icon="👁" label="Alcance" value={analytics.totals.reach} growth={analytics.growth.reach} color="#06b6d4" />
        <StatCard icon="❤️" label="Likes" value={analytics.totals.likes} growth={analytics.growth.likes} color="#E1306C" />
        <StatCard icon="💬" label="Comentarios" value={analytics.totals.comments} growth={analytics.growth.comments} color="#f59e0b" />
        <StatCard icon="🔁" label="Compartidos" value={analytics.totals.shares} color="#10b981" />
        <StatCard icon="▶️" label="Reproducciones" value={analytics.totals.video_views} color="#FF0000" />
      </div>

      {/* Charts row */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px' }}>
        {/* Area chart */}
        <Card>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '15px', fontWeight: '700', marginBottom: '20px' }}>Alcance y Likes en el tiempo</h3>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={analytics.chart}>
              <defs>
                <linearGradient id="reachG" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#7c3aed" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#7c3aed" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="likesG" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#E1306C" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#E1306C" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" tick={{ fill: '#50506a', fontSize: 10 }} axisLine={false} tickLine={false} interval={6} />
              <YAxis tick={{ fill: '#50506a', fontSize: 10 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '10px', fontSize: '12px' }} />
              <Area type="monotone" dataKey="reach" stroke="#7c3aed" strokeWidth={2} fill="url(#reachG)" name="Alcance" />
              <Area type="monotone" dataKey="likes" stroke="#E1306C" strokeWidth={2} fill="url(#likesG)" name="Likes" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        {/* Pie chart */}
        <Card>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '15px', fontWeight: '700', marginBottom: '20px' }}>Distribución por red</h3>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" paddingAngle={3}>
                {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
              </Pie>
              <Tooltip contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '10px', fontSize: '12px' }} formatter={(v) => v.toLocaleString('es')} />
            </PieChart>
          </ResponsiveContainer>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '8px' }}>
            {pieData.map((d, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
                <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: d.color, flexShrink: 0 }} />
                <span style={{ color: 'var(--text-secondary)', flex: 1 }}>{d.name}</span>
                <span style={{ fontWeight: '600' }}>{(d.value / 1000).toFixed(1)}K</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Bar chart - posts per network */}
      <Card>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '15px', fontWeight: '700', marginBottom: '20px' }}>Posts publicados por red</h3>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={analytics.by_network} barSize={32}>
            <XAxis dataKey="network" tick={{ fill: '#50506a', fontSize: 11 }} axisLine={false} tickLine={false}
              tickFormatter={v => NETWORK_CONFIG[v]?.name || v} />
            <YAxis tick={{ fill: '#50506a', fontSize: 11 }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '10px', fontSize: '12px' }}
              labelFormatter={v => NETWORK_CONFIG[v]?.name || v} />
            <Bar dataKey="posts" radius={[6, 6, 0, 0]} name="Posts">
              {analytics.by_network.map((entry, i) => (
                <Cell key={i} fill={NETWORK_CONFIG[entry.network]?.color || COLORS[i % COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* Network performance table */}
      <Card>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '15px', fontWeight: '700', marginBottom: '16px' }}>Rendimiento por red social</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {analytics.by_network.map(net => {
            const cfg = NETWORK_CONFIG[net.network];
            return (
              <div key={net.network} style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '14px', borderRadius: 'var(--radius-md)', background: 'var(--bg-elevated)', border: '1px solid var(--border)' }}>
                <NetworkIcon network={net.network} size={36} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '4px' }}>{cfg?.name}</div>
                  <ProgressBar value={net.engagement * 10} color={cfg?.color} height={4} />
                </div>
                <div style={{ display: 'flex', gap: '20px', flexShrink: 0 }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '15px', fontWeight: '700' }}>{net.posts}</div>
                    <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Posts</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '15px', fontWeight: '700' }}>{(net.reach / 1000).toFixed(1)}K</div>
                    <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Alcance</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '15px', fontWeight: '700' }}>{net.likes.toLocaleString('es')}</div>
                    <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Likes</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <Badge color={cfg?.color}>{net.engagement}%</Badge>
                    <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '3px' }}>Eng.</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>
      {/* Mejor hora por red */}
      {Object.keys(bestHours).length > 0 && (
        <Card>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '15px', fontWeight: '700', marginBottom: '16px' }}>⏰ Mejor hora para publicar</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '12px' }}>
            {Object.entries(bestHours).map(([network, data]) => {
              const cfg = NETWORK_CONFIG[network];
              const hour = data.best_hour;
              const ampm = hour >= 12 ? 'PM' : 'AM';
              const h12 = hour % 12 || 12;
              return (
                <div key={network} style={{ padding: '14px', borderRadius: 'var(--radius-md)', background: 'var(--bg-elevated)', border: '1px solid var(--border)', textAlign: 'center' }}>
                  <NetworkIcon network={network} size={28} />
                  <div style={{ fontWeight: '800', fontSize: '22px', margin: '8px 0 2px' }}>{h12}:00 {ampm}</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{data.avg_likes} likes promedio</div>
                  <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>{data.sample} muestra{data.sample !== 1 ? 's' : ''}</div>
                </div>
              );
            })}
          </div>
          <p style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '12px' }}>
            Basado en el historial de publicaciones con mayor engagement de tu cuenta.
          </p>
        </Card>
      )}

    </div>
  );
}
